import {
  type MessageSequenceStatus,
  MessageSort,
  WelcomeMessageApiV3RequestDto,
  WelcomeMessageScope,
  WelcomeMessagesService,
} from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { $creatorId } from '@/models/creators.ts'
import { createQueryProvider } from '@/modules/auto-query-context'
import type { WelcomeMessage } from '@/modules/creator-settings/types.ts'
import { useWelcomeMessageService } from '@/modules/creator-settings/welcome-messages/service/welcome-message-fetch-context'
import type { Message } from '@/modules/message/service/message-service'
import { getErrorMessage, handleError } from '@/utils/error'

export function useWelcomeMessages(
  scope: WelcomeMessageScope,
  sortOption: MessageSort = MessageSort.DEFAULT,
) {
  const creatorId = useUnit($creatorId)

  const { getListMessages } = useWelcomeMessageService()

  return useQuery(
    ['welcomeMessages', creatorId, scope, sortOption],
    () => getListMessages(creatorId!, scope, sortOption),
    {
      enabled: !!creatorId,
    },
  )
}

export function useGetWelcomeMessageById(
  messageId: string | undefined,
  scope: WelcomeMessageScope,
) {
  const creatorId = useUnit($creatorId)

  const { getMessageById } = useWelcomeMessageService()

  return useQuery(
    ['welcomeMessageById', creatorId, scope, messageId],
    () => {
      if (!messageId) {
        return null
      }

      return getMessageById(creatorId!, scope, messageId)
    },
    {
      enabled: !!creatorId,
    },
  )
}

export function useCreateWelcomeMessage(
  scope: WelcomeMessageScope,
  sortOption: MessageSort = MessageSort.DEFAULT,
) {
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)

  const toast = useToast()

  return useMutation(
    (message: WelcomeMessageApiV3RequestDto) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return WelcomeMessagesService.createWelcomeMessageV3({
        creatorId,
        scope,
        requestBody: message,
      })
    },
    {
      onSuccess: () => {
        toast.success('Successfully created')
      },
      onError: (error) => {
        const detail = getErrorMessage(error)
        const text = detail?.toLowerCase().includes('timeframe')
          ? detail
          : 'Failed to create the message.'

        handleError(text)
      },
      onSettled: () => {
        client.invalidateQueries([
          'welcomeMessages',
          creatorId,
          scope,
          sortOption,
        ])
      },
    },
  )
}

export function useUpdateWelcomeMessage(
  scope: WelcomeMessageScope,
  sortOption: MessageSort = MessageSort.DEFAULT,
) {
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)

  const toast = useToast()

  return useMutation(
    (body: { id: number; message: WelcomeMessageApiV3RequestDto }) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }
      const { id, message } = body

      return WelcomeMessagesService.updateWelcomeMessage({
        creatorId,
        scope,
        messageId: id,
        requestBody: message,
      })
    },
    {
      onSuccess: (responseData) => {
        toast.success('Successfully updated')
      },
      onError: (error) => {
        const detail = getErrorMessage(error)
        const text = detail?.toLowerCase().includes('timeframe')
          ? detail
          : 'Failed to create the message.'

        handleError(text)
      },
      onSettled: () => {
        client.invalidateQueries([
          'welcomeMessages',
          creatorId,
          scope,
          sortOption,
        ])
      },
    },
  )
}

export function useDeleteWelcomeMessage(
  scope: WelcomeMessageScope,
  sortOption: MessageSort = MessageSort.DEFAULT,
) {
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)

  const { deleteMessage } = useWelcomeMessageService()
  const toast = useToast()

  return useMutation(
    (messageId: number) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return deleteMessage(creatorId, scope, messageId)
    },
    {
      onMutate: (messageId) => {
        client.setQueryData(
          ['welcomeMessages', creatorId, scope, sortOption],
          (state: unknown) => {
            const messages = (state as Message[]) ?? []

            return messages.filter((m) => m.id !== messageId)
          },
        )
      },
      onSuccess: () => {
        toast.success('Successfully deleted')
      },
      onSettled: () => {
        client.invalidateQueries([
          'welcomeMessages',
          creatorId,
          scope,
          sortOption,
        ])
      },
      onError: (error) => {
        handleError(error, 'Failed to delete the message')
      },
    },
  )
}

export function useToggleWelcomeMessageByMessagId(
  scope: WelcomeMessageScope,
  sortOption: MessageSort = MessageSort.DEFAULT,
) {
  const toast = useToast()
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)

  const { updateWelcomeMessageToggle } = useWelcomeMessageService()

  return useMutation(
    ({
      messageId,
      value,
    }: {
      messageId: string
      value: MessageSequenceStatus
    }) => {
      if (!creatorId) {
        throw new Error('Creator not selected')
      }

      return updateWelcomeMessageToggle(creatorId, messageId, scope, value)
    },
    {
      onMutate: (message) => {
        client.setQueryData<WelcomeMessage[] | undefined>(
          ['welcomeMessages', creatorId, scope, sortOption],
          (state) => {
            if (!state) {
              return state
            }

            return state.map((m) =>
              String(m.id) === message.messageId
                ? {
                    ...m,
                    sequence: {
                      ...m.sequence,
                      status: message.value,
                    },
                  }
                : m,
            )
          },
        )
      },
      onSuccess: () => {
        toast.success('Successfully updated')
      },
      onError: (error) => {
        handleError(error, 'Failed to update the message')
        client.invalidateQueries([
          'welcomeMessages',
          creatorId,
          scope,
          sortOption,
        ])
      },
    },
  )
}

export const { WelcomeMessagesQueryProvider } = createQueryProvider(
  'WelcomeMessagesQuery',
  () => useWelcomeMessages(WelcomeMessageScope.NEW_FANS),
)
